import {
  addDoc,
  collection,
  deleteDoc,
  disableNetwork,
  doc,
  documentId,
  enableNetwork,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
  startAfter,
  updateDoc,
  writeBatch
} from 'firebase/firestore';

// Cache for frequently accessed documents
const documentCache = new Map();
const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_SIZE = 100;

// Performance monitoring
const performanceMetrics = {
  queries: 0,
  cacheHits: 0,
  totalTime: 0,
  errors: 0,
};

// Cache management utilities
const getCacheKey = (collectionName, id, queryParams = '') => {
  return `${collectionName}:${id || 'collection'}:${queryParams}`;
};

const isValidCacheEntry = (entry) => {
  return entry && (Date.now() - entry.timestamp) < CACHE_EXPIRY;
};

const cleanupCache = () => {
  if (documentCache.size > MAX_CACHE_SIZE) {
    const entries = Array.from(documentCache.entries());
    // Remove oldest 20% of entries
    const toRemove = Math.floor(MAX_CACHE_SIZE * 0.2);
    entries
      .sort((a, b) => a[1].timestamp - b[1].timestamp)
      .slice(0, toRemove)
      .forEach(([key]) => documentCache.delete(key));
  }
};

const setCacheEntry = (key, data) => {
  cleanupCache();
  documentCache.set(key, {
    data,
    timestamp: Date.now(),
  });
};

// Enhanced error handling
const handleFirestoreError = (error, context = '') => {
  performanceMetrics.errors++;
  console.error(`Firestore error${context ? ` (${context})` : ''}:`, error);

  // Enhanced error reporting
  if (error.code) {
    switch (error.code) {
      case 'permission-denied':
        console.error('Permission denied. Check Firestore security rules.');
        break;
      case 'unavailable':
        console.error('Firestore service unavailable. Check network connection.');
        break;
      case 'deadline-exceeded':
        console.error('Request deadline exceeded. Consider optimizing queries.');
        break;
      default:
        console.error('Firestore error code:', error.code);
    }
  }

  return null;
};

// Optimized getData with caching and better error handling
const getData = async (
  setData,
  setRetrieved,
  docPath,
  firestore,
  userProfile,
  useCache = true
) => {
  const startTime = Date.now();
  performanceMetrics.queries++;

  try {
    const db = firestore.instance;
    const collection = firestore.user.collectionName;
    const uid = userProfile.attributes.uid;
    const fullPath = docPath || `${collection}/${uid}`;

    // Check cache first
    if (useCache) {
      const cacheKey = getCacheKey('user-doc', fullPath);
      const cached = documentCache.get(cacheKey);
      if (isValidCacheEntry(cached)) {
        performanceMetrics.cacheHits++;
        if (setData) setData(cached.data);
        if (setRetrieved) setRetrieved(true);
        return cached.data;
      }
    }

    const fullCollection = doc(db, fullPath);
    const docSnap = await getDoc(fullCollection);

    let data = {};
    if (docSnap.exists()) {
      data = { ...docSnap.data(), id: docSnap.id };
    }

    // Cache the result
    if (useCache) {
      const cacheKey = getCacheKey('user-doc', fullPath);
      setCacheEntry(cacheKey, data);
    }

    if (setData) setData(data);
    if (setRetrieved) setRetrieved(true);

    performanceMetrics.totalTime += Date.now() - startTime;
    return data;

  } catch (error) {
    handleFirestoreError(error, 'getData');
    if (setData) setData({});
    if (setRetrieved) setRetrieved(true);
    return {};
  }
};

// Optimized updateData with better error handling and validation
const updateData = async (docPath, firestore, data, options = {}) => {
  const startTime = Date.now();
  performanceMetrics.queries++;

  try {
    // Input validation
    if (!docPath || !data || typeof data !== 'object') {
      throw new Error('Invalid parameters: docPath and data are required');
    }

    const db = firestore.instance;
    const fullCollection = doc(db, docPath);

    // Add timestamp if not provided
    const updateData = {
      ...data,
      updatedAt: data.updatedAt || new Date().toISOString(),
    };

    await updateDoc(fullCollection, updateData);

    // Invalidate cache for this document
    const pathParts = docPath.split('/');
    if (pathParts.length >= 2) {
      const collectionName = pathParts[0];
      const docId = pathParts[1];
      const cacheKey = getCacheKey(collectionName, docId);
      documentCache.delete(cacheKey);
    }

    performanceMetrics.totalTime += Date.now() - startTime;
    console.log('Document successfully updated!');
    return { success: true, path: docPath };

  } catch (error) {
    handleFirestoreError(error, 'updateData');
    throw error; // Re-throw for caller to handle
  }
};

// Enhanced saveDocument with better error handling and validation
export const saveDocument = async (firestore, collectionPath, data, options = {}) => {
  const startTime = Date.now();
  performanceMetrics.queries++;

  try {
    // Input validation
    if (!collectionPath || !data || typeof data !== 'object') {
      throw new Error('Invalid parameters: collectionPath and data are required');
    }
    let id = options?.id;
    const collectionRef = collection(firestore.instance, collectionPath);

    // Add timestamps
    const documentData = {
      ...data,
      createdAt: data.createdAt || new Date().toISOString(),
      updatedAt: data.updatedAt || new Date().toISOString(),
    };
    const docRef = id
      ? await setDoc(doc(collectionRef, id), documentData)
      : await addDoc(collectionRef, documentData);
    id = id || docRef.id;
    performanceMetrics.totalTime += Date.now() - startTime;
    return {
      ...docRef,
      doc_id: id,
      id: id,
      success: true,
      data: { ...documentData, id: id }
    };

  } catch (error) {
    handleFirestoreError(error, 'saveDocument');
    throw error; // Re-throw for caller to handle
  }
};

// New: Batch operations for better performance
const saveBatchDocuments = async (firestore, operations) => {
  const startTime = Date.now();
  performanceMetrics.queries++;

  try {
    const db = firestore.instance;
    const batch = writeBatch(db);
    const results = [];

    operations.forEach(({ collectionPath, data, docId }) => {
      const docRef = docId
        ? doc(db, collectionPath, docId)
        : doc(collection(db, collectionPath));

      const documentData = {
        ...data,
        createdAt: data.createdAt || new Date().toISOString(),
        updatedAt: data.updatedAt || new Date().toISOString(),
      };

      batch.set(docRef, documentData);
      results.push({ docRef, data: documentData });
    });

    await batch.commit();

    performanceMetrics.totalTime += Date.now() - startTime;
    return { success: true, results };

  } catch (error) {
    handleFirestoreError(error, 'saveBatchDocuments');
    throw error;
  }
};
// Enhanced getPaginatedData with better error handling and flexibility
export const getPaginatedData = async (
  startAfterDoc,
  docsLimit,
  collectionName,
  firestore,
  orderDirection = 'desc',
  orderField = null,
  wheres = []
) => {
  const startTime = Date.now();
  performanceMetrics.queries++;

  try {
    const collectionRef = collection(firestore.instance, collectionName);
    
    // Build query constraints array
    const queryConstraints = [...wheres, limit(docsLimit)];
    if (orderDirection && orderField) {
      queryConstraints.push(orderBy(orderField, orderDirection));
      if (startAfterDoc) {
        queryConstraints.push(startAfter(startAfterDoc[orderField]));
      }
    } else if (startAfterDoc) {
      // No explicit orderField — fall back to document-ID ordering
      queryConstraints.push(orderBy(documentId(), orderDirection ?? 'asc'));
      queryConstraints.push(startAfter(startAfterDoc.id));
    }

    const q = query(collectionRef, ...queryConstraints);
    const snapshot = await getDocs(q);
    const results = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    performanceMetrics.totalTime += Date.now() - startTime;
    return results;

  } catch (error) {
    handleFirestoreError(error, 'getPaginatedData');
    return [];
  }
};

// Optimized getDataFromCollection with caching and better performance
export const getDataFromCollection = async (setData, setDataRetrieved, db, collectionName, useCache = true, limitCount = 10) => {
  return getDataFromCollectionWithQuery(setData, setDataRetrieved, db, collectionName, [], useCache, limitCount);
};

export const getDataFromCollectionWithQuery = async (
  setData,
  setDataRetrieved,
  db,
  collectionName,
  wheres = [],
  useCache = true,
  limitCount = 10
) => {
  const startTime = Date.now();
  performanceMetrics.queries++;

  try {
    if (setDataRetrieved) {
      setDataRetrieved(false);
    }

    // Create cache key based on collection, query, and limit
    const queryKey = wheres.length > 0 ? JSON.stringify(wheres) : '';
    const cacheKey = getCacheKey(collectionName, 'collection', `${queryKey}:limit:${limitCount}`);

    // Check cache first
    if (useCache) {
      const cached = documentCache.get(cacheKey);
      if (isValidCacheEntry(cached)) {
        performanceMetrics.cacheHits++;
        if (setData) setData(cached.data);
        if (setDataRetrieved) setDataRetrieved(true);
        return cached.data;
      }
    }

    const collectionRef = collection(db, collectionName);

    // Build query with limit and optional where clauses
    let queryRef;
    if (wheres.length > 0) {
      queryRef = query(collectionRef, ...wheres, limit(limitCount));
    } else {
      queryRef = query(collectionRef, limit(limitCount));
    }

    const querySnapshot = await getDocs(queryRef);
    const documentsMap = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data()
    }));

    // Cache the result
    if (useCache) {
      setCacheEntry(cacheKey, documentsMap);
    }

    if (setData) {
      setData(documentsMap);
    }
    if (setDataRetrieved) {
      setDataRetrieved(true);
    }

    performanceMetrics.totalTime += Date.now() - startTime;
    return documentsMap;

  } catch (error) {
    handleFirestoreError(error, 'getDataFromCollectionWithQuery');
    if (setData) setData([]);
    if (setDataRetrieved) setDataRetrieved(true);
    return [];
  }
};

// Optimized getDocById with caching and better error handling
export const getDocById = async (setDoc, setDocRetrieved, db, collectionName, id, useCache = true) => {
  const startTime = Date.now();
  performanceMetrics.queries++;
  try {
    if (setDocRetrieved) {
      setDocRetrieved(false);
    }
    // Input validation
    if (!id || !collectionName) {
      throw new Error('Invalid parameters: collectionName and id are required');
    }
    // Check cache first
    if (useCache) {
      const cacheKey = getCacheKey(collectionName, id);
      const cached = documentCache.get(cacheKey);
      if (isValidCacheEntry(cached)) {
        performanceMetrics.cacheHits++;
        if (setDoc) setDoc(cached.data);
        if (setDocRetrieved) setDocRetrieved(true);
        return cached.data;
      }
    }
    const docRef = doc(db, collectionName, id);
    const docSnap = await getDoc(docRef);

    let finalData = null;
    if (docSnap.exists()) {
      finalData = { ...docSnap.data(), id: docSnap.id };

      // Cache the result
      if (useCache) {
        const cacheKey = getCacheKey(collectionName, id);
        setCacheEntry(cacheKey, finalData);
      }
    }

    if (setDoc) {
      setDoc(finalData);
    }
    if (setDocRetrieved) {
      setDocRetrieved(true);
    }

    performanceMetrics.totalTime += Date.now() - startTime;
    return finalData;

  } catch (error) {
    handleFirestoreError(error, 'getDocById');
    if (setDoc) setDoc(null);
    if (setDocRetrieved) setDocRetrieved(true);
    return null;
  }
};

// Utility function for number formatting (unchanged for backward compatibility)
export function numberWithCommas(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

// New: Performance monitoring utilities
export const getPerformanceMetrics = () => ({ ...performanceMetrics });

export const resetPerformanceMetrics = () => {
  performanceMetrics.queries = 0;
  performanceMetrics.cacheHits = 0;
  performanceMetrics.totalTime = 0;
  performanceMetrics.errors = 0;
};

// New: Cache management utilities
export const clearCache = () => {
  documentCache.clear();
};

export const getCacheStats = () => ({
  size: documentCache.size,
  maxSize: MAX_CACHE_SIZE,
  expiryTime: CACHE_EXPIRY,
});

// New: Batch operations for multiple documents
export const getMultipleDocsById = async (db, requests, useCache = true) => {
  const startTime = Date.now();
  performanceMetrics.queries++;

  try {
    const results = await Promise.all(
      requests.map(async ({ collectionName, id }) => {
        try {
          return await getDocById(null, null, db, collectionName, id, useCache);
        } catch (error) {
          handleFirestoreError(error, `getMultipleDocsById - ${collectionName}/${id}`);
          return null;
        }
      })
    );

    performanceMetrics.totalTime += Date.now() - startTime;
    return results;

  } catch (error) {
    handleFirestoreError(error, 'getMultipleDocsById');
    return [];
  }
};

// New: Network status utilities
export const enableFirestoreNetwork = async (db) => {
  try {
    await enableNetwork(db);
    console.log('Firestore network enabled');
    return true;
  } catch (error) {
    handleFirestoreError(error, 'enableNetwork');
    return false;
  }
};

export const disableFirestoreNetwork = async (db) => {
  try {
    await disableNetwork(db);
    console.log('Firestore network disabled');
    return true;
  } catch (error) {
    handleFirestoreError(error, 'disableNetwork');
    return false;
  }
};

// Simple document fetcher that returns a promise directly
export const getDocumentById = async (db, collectionName, id, useCache = true) => {
  return new Promise((resolve, reject) => {
    getDocById(
      (doc) => resolve(doc),
      (retrieved) => {
        // This callback is called when retrieval is complete
        // If doc was already resolved, do nothing
      },
      db,
      collectionName,
      id,
      useCache
    ).catch(reject);
  });
};

// Delete document by ID with proper error handling and cache invalidation
// Supports soft delete via options.softDelete flag
export const deleteDocumentById = async (db, collectionName, id, options = {}) => {
  const startTime = Date.now();
  performanceMetrics.queries++;

  try {
    // Input validation
    if (!id || !collectionName) {
      throw new Error('Invalid parameters: collectionName and id are required');
    }

    const docRef = doc(db, collectionName, id);

    if (options.softDelete) {
      // Soft delete: Mark document as deleted without removing it
      const deleteData = {
        is_deleted: true,
        deleted_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      await updateDoc(docRef, deleteData);
    } else {
      // Hard delete: Actually remove the document
      await deleteDoc(docRef);
    }

    // Invalidate cache for this document
    const cacheKey = getCacheKey(collectionName, id);
    documentCache.delete(cacheKey);

    // Also invalidate collection cache that might include this document
    const collectionCacheKey = getCacheKey(collectionName, 'collection');
    documentCache.delete(collectionCacheKey);

    performanceMetrics.totalTime += Date.now() - startTime;
    return { 
      success: true, 
      id, 
      collectionName,
      softDelete: options.softDelete || false
    };

  } catch (error) {
    handleFirestoreError(error, 'deleteDocumentById');
    throw error; // Re-throw for caller to handle
  }
};

// Export updateData as a named export
export { updateData };

// Backward compatible default export
const firestoreUtils = {
  getData,
  updateData,
  saveDocument,
  getPaginatedData,
  getDataFromCollection,
  getDocumentById,
  deleteDocumentById,
  // New optimized functions
  saveBatchDocuments,
  getMultipleDocsById,
  clearCache,
  getCacheStats,
  getPerformanceMetrics,
  resetPerformanceMetrics,
  enableFirestoreNetwork,
  disableFirestoreNetwork,
};

export default firestoreUtils;
